import React from "react";
import Modal from "../../../../components/Modal";
import InputField from "../../../../components/InputField";
import DeptMultiSelect from "../DeptMultiSelect";
import {
  SPOC_FIELDS,
  getSpocLobOptions,
  getSpocDeptOptions,
  getSpocRoleOptions,
  isMasterAdminUser,
} from "../../SpocFields";
import "./index.scss";

// ── Helpers ───────────────────────────────────────────────────────────────────
const getSelectOptions = (field, formData, roleOptions) => {
  if (field.dynamicLob) return getSpocLobOptions();
  if (field.dynamicRole || field.name === "role") {
    return roleOptions && roleOptions.length > 0
      ? roleOptions
      : getSpocRoleOptions();
  }
  return field.options || [];
};

const getOptionLabel = (options, value) => {
  if (!value) return "—";
  const match = options.find(
    (opt) => opt.value?.toLowerCase() === value?.toLowerCase(),
  );
  return match?.label || value;
};

// ── Modal ─────────────────────────────────────────────────────────────────────
const AddNewSpocModal = ({
  isOpen,
  onClose,
  mode = "add",
  formData,
  formErrors,
  saving,
  onChange,
  onDeptToggle,
  onSubmit,
  roleOptions,
  isMasterAdmin,
}) => {
  const isView = mode === "view";
  const isEdit = mode === "edit";

  const masterAdminActive =
    typeof isMasterAdmin === "boolean" ? isMasterAdmin : isMasterAdminUser();

  const title =
    mode === "add"
      ? "Add New SPOC"
      : mode === "edit"
        ? "Edit SPOC"
        : "SPOC Details";

  const isFieldEditable = (field) => {
    if (isView) return false;
    if (isEdit) return field.editableOnEdit === true;
    return true;
  };

  const isSpocRole =
    String(formData?.role || "").trim().toLowerCase() === "spoc";

  // When master admin is logged in: show LOB & Departments only if SPOC role is selected
  const shouldHideLobAndDept = masterAdminActive && !isSpocRole;

  const visibleFields = SPOC_FIELDS.filter(
    (f) =>
      !(isView && f.hideOnView) &&
      !(isEdit && f.hideOnEdit) &&
      !(isEdit && f.hideOnView) &&
      !(shouldHideLobAndDept && (f.name === "lob" || f.name === "departments")),
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="medium">
      <form
        className="spoc-modal-body"
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          if (!isView && !saving) onSubmit();
        }}
      >
        <div className="spoc-modal-form">
          {visibleFields.map((field) => {
            const editable = isFieldEditable(field);

            // ── Multi-select: AA Department ───────────────────────────────
            if (field.type === "multiselect") {
              const deptOptions = getSpocDeptOptions(formData.lob);
              const selectedDepts = Array.isArray(formData.departments)
                ? formData.departments
                : [];

              return (
                <div className="spoc-form-group" key={field.name}>
                  {!editable ? (
                    /* View / read-only — show tags only */
                    <>
                      <label className="spoc-form-group__label">
                        {field.label}
                      </label>
                      <div className="spoc-form-group__readonly spoc-form-group__readonly--tags">
                        {selectedDepts.length === 0 ? (
                          <span style={{ color: "#9ca3af" }}>—</span>
                        ) : (
                          selectedDepts.map((d, i) => (
                            <span
                              key={i}
                              className="spoc-dept-tag spoc-dept-tag--readonly"
                            >
                              {d}
                            </span>
                          ))
                        )}
                      </div>
                    </>
                  ) : (
                    /* Editable — use the standalone DeptMultiSelect component */
                    <DeptMultiSelect
                      label={field.label}
                      required={field.required}
                      options={deptOptions}
                      selected={selectedDepts}
                      onToggle={onDeptToggle}
                      placeholder={
                        !formData.lob
                          ? "Select a LOB first"
                          : "Select one or more departments"
                      }
                      disabled={!formData.lob}
                      error={!!formErrors[field.name]}
                      errorMsg={formErrors[field.name]}
                    />
                  )}
                </div>
              );
            }

            // ── Regular select ────────────────────────────────────────────
            if (field.type === "select") {
              const selectOptions = getSelectOptions(
                field,
                formData,
                roleOptions,
              );
              return (
                <div className="spoc-form-group" key={field.name}>
                  <label className="spoc-form-group__label">
                    {field.label}
                    {field.required && <span className="spoc-required">*</span>}
                  </label>
                  {!editable ? (
                    <div className="spoc-form-group__readonly">
                      {getOptionLabel(selectOptions, formData[field.name])}
                    </div>
                  ) : (
                    <select
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={onChange}
                      className={`spoc-form-group__select${
                        formErrors[field.name]
                          ? " spoc-form-group__select--error"
                          : ""
                      }`}
                    >
                      {selectOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  )}
                  {formErrors[field.name] && (
                    <p className="spoc-form-group__error">
                      {formErrors[field.name]}
                    </p>
                  )}
                </div>
              );
            }

            // ── Text / email / password ───────────────────────────────────
            return (
              <div className="spoc-form-group" key={field.name}>
                {!editable ? (
                  <>
                    <label className="spoc-form-group__label">
                      {field.label}
                    </label>
                    <div className="spoc-form-group__readonly">
                      {formData[field.name] || "—"}
                    </div>
                  </>
                ) : (
                  <InputField
                    label={field.label}
                    name={field.name}
                    type={field.type}
                    value={formData[field.name] || ""}
                    onChange={onChange}
                    placeholder={field.placeholder}
                    required={field.required}
                    error={formErrors[field.name]}
                    autoComplete={
                      field.type === "password" ? "new-password" : "off"
                    }
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="spoc-modal-footer">
          <button
            type="button"
            className="spoc-btn spoc-btn--cancel"
            onClick={onClose}
            disabled={saving}
          >
            {isView ? "Close" : "Cancel"}
          </button>
          {!isView && (
            <button
              type="submit"
              className="spoc-btn spoc-btn--submit"
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spoc-btn__spinner" />
                  Saving…
                </>
              ) : mode === "edit" ? (
                "Save Changes"
              ) : (
                "Create SPOC"
              )}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default AddNewSpocModal;
